import React, { useState, useEffect } from "react";
import BaseModal from "components/BaseModal";
import { useSession } from "hooks/useSession";
import { showMainToast } from "utils/showToast";

export default function ProviderFormModal({ show, onClose, mode, userData, onSuccess }) {
  const [errors, setErrors] = useState({});
  const [apiList, setApiList] = useState([]);
  const [comTypes, setComTypes] = useState([]);
  const { userid } = useSession();

  // ✅ Controlled form state
  const [formData, setFormData] = useState({
    name: "", re_1: "", re_2: "", re_3: "", re_4: "",
    api_id: "", type: "", status: "", com_type: "",
  });

  // ✅ Reset form whenever userData or mode changes
  useEffect(() => {
    if (mode === "edit" && userData) {
      setFormData({
        name: userData.name || "",
        re_1: userData.re_1 || "",
        re_2: userData.re_2 || "",
        re_3: userData.re_3 || "",
        re_4: userData.re_4 || "",
        api_id: userData.api_id || "",
        type: userData.type || "",
        status: userData.status || "",
        com_type: userData.com_type || "",
      });
    } else {
      setFormData({ name: "", re_1: "", re_2: "", re_3: "", re_4: "", api_id: "", type: "", status: "", com_type: "" });
    }
    setErrors({});
  }, [userData, mode, show]); // ✅ `show` ensures reset when modal reopens

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const apiForm = new FormData();
        apiForm.append("action", "GET");
        apiForm.append("userid", userid);
        const apiRes = await fetch("https://api.mokshiq.in/api_manager_action", {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
          body: apiForm,
        });
        const apiData = await apiRes.json();
        setApiList(apiData.data || []);

        const comForm = new FormData();
        comForm.append("action", "GET");
        comForm.append("userid", userid);
        const comRes = await fetch("https://api.mokshiq.in/commission_type_manager_action", {
          method: "POST",
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
          body: comForm,
        });
        const comData = await comRes.json();
        setComTypes(comData.data || []);
      } catch (err) {
        console.error("Error fetching dropdowns:", err);
      }
    };
    fetchDropdowns();
  }, []);

  // ✅ Single handler for all fields
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const validateForm = () => {
    const requiredFields = ["name", "api_id", "type", "status", "com_type"];
    let newErrors = {};
    requiredFields.forEach((field) => {
      if (!formData[field]?.trim()) newErrors[field] = `${field} is required.`;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showMainToast("Please correct the errors before saving.", "danger");
      return;
    }

    const payload = new FormData();
    Object.entries(formData).forEach(([key, val]) => payload.append(key, val));
    payload.append("id", userData?.id || "");
    payload.append("action", "ADDUpdate");
    payload.append("userid", userid);

    try {
      const response = await fetch("https://api.mokshiq.in/provider_manager_action", {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        body: payload,
      });
      const result = await response.json();
      if (result.status) {
        showMainToast(result.data || "Success!", "success");
        onClose();
        if (onSuccess) onSuccess();
      } else {
        showMainToast(result.data || "Something went wrong!", "danger");
      }
    } catch {
      showMainToast("Something went wrong!", "danger");
    }
  };

  return (
    <BaseModal
      show={show}
      onClose={onClose}
      title={mode === "edit" ? "Edit Provider" : "Add Provider"}
      footerButtons={[
        { label: "Cancel", className: "btn-secondary", dismiss: true },
        { label: mode === "edit" ? "Update" : "Save", className: "btn-primary", onClick: handleSave },
      ]}
    >
      <div className="card-body">
        <form className="row g-3 app-form rounded-control">
          {[["name", "Name"], ["re_1", "RE 1"], ["re_2", "RE 2"], ["re_3", "RE 3"], ["re_4", "RE 4"]].map(([id, label]) => (
            <div className="col-md-6" key={id}>
              <label className="form-label">{label}</label>
              <input
                id={id}
                className={`form-control ${errors[id] ? "is-invalid" : ""}`}
                value={formData[id]}
                onChange={handleChange}
              />
              {errors[id] && <div className="text-danger">{errors[id]}</div>}
            </div>
          ))}

          {/* API dropdown */}
          <div className="col-md-6">
            <label className="form-label">API</label>
            <select id="api_id" className={`form-select ${errors.api_id ? "is-invalid" : ""}`}
              value={formData.api_id} onChange={handleChange}>
              <option value="">-- Select API --</option>
              {apiList.map((api) => (
                <option key={api.id} value={api.id}>{api.Name}</option>
              ))}
            </select>
            {errors.api_id && <div className="text-danger">{errors.api_id}</div>}
          </div>

          {/* Commission Type dropdown */}
          <div className="col-md-6">
            <label className="form-label">Commission Type</label>
            <select id="com_type" className={`form-select ${errors.com_type ? "is-invalid" : ""}`}
              value={formData.com_type} onChange={handleChange}>
              <option value="">-- Select Commission Type --</option>
              {comTypes.map((c) => (
                <option key={c.id} value={c.id}>{c.Name}</option>
              ))}
            </select>
            {errors.com_type && <div className="text-danger">{errors.com_type}</div>}
          </div>

          {/* Type dropdown */}
          <div className="col-md-6">
            <label className="form-label">Type</label>
            <select id="type" className={`form-select ${errors.type ? "is-invalid" : ""}`}
              value={formData.type} onChange={handleChange}>
              <option value="">-- Select Type --</option>
              <option value="fixed">Fixed</option>
              <option value="percent">Percentage</option>
            </select>
            {errors.type && <div className="text-danger">{errors.type}</div>}
          </div>

          {/* Status dropdown */}
          <div className="col-md-6">
            <label className="form-label">Status</label>
            <select id="status" className={`form-select ${errors.status ? "is-invalid" : ""}`}
              value={formData.status} onChange={handleChange}>
              <option value="">-- Select Status --</option>
              <option value="True">Active</option>
              <option value="False">Inactive</option>
            </select>
            {errors.status && <div className="text-danger">{errors.status}</div>}
          </div>
        </form>
      </div>
    </BaseModal>
  );
}